// TS shapes for the agent-graph events emitted by
// src/lifestyle_api/agent_graph_events.py. Keep these in lockstep
// with CANONICAL_NODES + the frame envelopes the unified-stream WS
// emits.

export type NodeState = "idle" | "active" | "done" | "failed";

export const NODES = [
  "triton",
  "your_twin",
  "partner_twin",
  "twin_3",
  "twin_4",
  "twin_5",
  "twin_6",
  "aristotle",
  "pact_broker",
  "negotiator",
  "settlement",
  "midnight",
  "crypto_wallet",
  "loyalty",
] as const;

export type NodeId = (typeof NODES)[number];

export interface GraphNodeEvent {
  topic_class: "graph";
  type: "graph.node_state";
  turn_id: string;
  node_id: NodeId;
  state: NodeState;
  label: string;
  metadata: Record<string, unknown>;
  at: number;
}

export interface GraphEdgeEvent {
  topic_class: "graph";
  type: "graph.edge_fire";
  turn_id: string;
  from_node_id: NodeId;
  to_node_id: NodeId;
  kind: string;
  payload_preview: string;
  metadata: Record<string, unknown>;
  at: number;
}

export interface PactEvent {
  topic_class: "pact";
  type:
    | "pact.broadcast"
    | "pact.bid"
    | "pact.drop"
    | "pact.no_proposals"
    | "pact.sealed";
  deal_id: string;
  [key: string]: unknown;
}

export interface UnifiedErrorEvent {
  topic_class: "error";
  code: string;
  message: string;
}

export type UnifiedEvent =
  | GraphNodeEvent
  | GraphEdgeEvent
  | PactEvent
  | UnifiedErrorEvent;

export function isGraphNodeEvent(e: UnifiedEvent): e is GraphNodeEvent {
  return e.topic_class === "graph" && e.type === "graph.node_state";
}

export function isGraphEdgeEvent(e: UnifiedEvent): e is GraphEdgeEvent {
  return e.topic_class === "graph" && e.type === "graph.edge_fire";
}

export function isPactEvent(e: UnifiedEvent): e is PactEvent {
  return e.topic_class === "pact";
}

// Node display metadata — carrier-aware labels filled in at render
// time by useCarrierContext.
export interface NodeDefinition {
  id: NodeId;
  label: string;
  description: string;
  lane: 0 | 1 | 2 | 3 | 4 | 5;     // vertical DAG lane (top → bottom)
}

export const NODE_DEFINITIONS: Record<NodeId, NodeDefinition> = {
  triton: {
    id: "triton",
    label: "You",
    description: "Where you type — your prompt enters the system here",
    lane: 0,
  },
  your_twin: {
    id: "your_twin",
    label: "Your Twin",
    description: "Your sovereign preferences",
    lane: 1,
  },
  partner_twin: {
    id: "partner_twin",
    label: "Partner Twin",
    description: "Partner-Fit negotiation peer",
    lane: 1,
  },
  twin_3: {
    id: "twin_3",
    label: "Twin",
    description: "Additional Twin peer for multi-party demos",
    lane: 1,
  },
  twin_4: {
    id: "twin_4",
    label: "Twin",
    description: "Additional Twin peer for multi-party demos",
    lane: 1,
  },
  twin_5: {
    id: "twin_5",
    label: "Twin",
    description: "Additional Twin peer for multi-party demos",
    lane: 1,
  },
  twin_6: {
    id: "twin_6",
    label: "Twin",
    description: "Additional Twin peer for multi-party demos",
    lane: 1,
  },
  aristotle: {
    id: "aristotle",
    label: "Aristotle",
    description: "Self-learning engine",
    lane: 2,
  },
  loyalty: {
    id: "loyalty",
    label: "Loyalty",
    description: "Points / balance",
    lane: 2,
  },
  pact_broker: {
    id: "pact_broker",
    label: "PACT Broker",
    description: "Agent-to-agent marketplace",
    lane: 3,
  },
  negotiator: {
    id: "negotiator",
    label: "Negotiator",
    description: "Ranks proposals + seals deals",
    lane: 4,
  },
  settlement: {
    id: "settlement",
    label: "Settlement",
    description: "Carrier settlement rail",
    lane: 5,
  },
  crypto_wallet: {
    id: "crypto_wallet",
    label: "Crypto Wallet",
    description: "Quorum-recovered keys",
    lane: 5,
  },
  midnight: {
    id: "midnight",
    label: "Midnight",
    description: "Trust + provenance chain",
    lane: 5,
  },
};
